import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RawMaterialSearch from "./RawMaterial-Search";
import RawMaterialDatatable from "./RawMaterialList-Datatable";
import RawMaterialService from "../../../services/RawMaterials/RawMaterialService";
import { Button } from "primereact/button";

type RawMaterialProductMapping = {
  rawMaterialId: number;
  productIds: number[];
  productNames: string[];
};

const RawMaterialsMainPage: React.FC = () => {
  const { t } = useTranslation();
  const [rawMaterials, setRawMaterials] = useState<any[]>([]);
  const [filteredRawMaterials, setFilteredRawMaterials] = useState<any[]>([]);
  const [productMappingByRawMaterialId, setProductMappingByRawMaterialId] = useState<
    Record<number, RawMaterialProductMapping>
  >({});
  const [selectedKeyword, setSelectedKeyword] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    handleSearch(selectedKeyword);
  }, [selectedKeyword, rawMaterials, productMappingByRawMaterialId]);

  useEffect(() => {
    void fetchRawMaterials();
  }, []);

  const normalizeProductMapping = (rawMaterialId: number, mapping: any): RawMaterialProductMapping => {
    const productIds = Array.isArray(mapping?.productIds)
      ? mapping.productIds.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0)
      : Array.isArray(mapping?.ProductIds)
        ? mapping.ProductIds.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0)
        : Array.isArray(mapping?.products)
          ? mapping.products
              .map((product: any) => Number(product.productId ?? product.productID ?? product.id ?? product.Id))
              .filter((id: number) => Number.isFinite(id) && id > 0)
          : mapping?.productId
            ? [Number(mapping.productId)]
            : [];

    const productNames = Array.isArray(mapping?.productNames)
      ? mapping.productNames.filter(Boolean)
      : Array.isArray(mapping?.ProductNames)
        ? mapping.ProductNames.filter(Boolean)
        : Array.isArray(mapping?.products)
          ? mapping.products
              .map((product: any) => product.productName ?? product.name ?? product.title)
              .filter(Boolean)
          : mapping?.productName
            ? [mapping.productName]
            : [];

    return {
      rawMaterialId,
      productIds,
      productNames,
    };
  };

  const fetchRawMaterials = async () => {
    try {
      const response = await RawMaterialService.getAllRawMaterials();
      const materials = Array.isArray(response.data) ? response.data : [];

      if (!isMountedRef.current) return;

      const mappings = await loadProductMappingsInBackground(materials);

      if (!isMountedRef.current) return;

      setProductMappingByRawMaterialId(mappings);
      setRawMaterials(materials);
      setFilteredRawMaterials(materials);
    } catch (error) {
      console.error("Error fetching raw materials:", error);
      setRawMaterials([]);
      setFilteredRawMaterials([]);
      setProductMappingByRawMaterialId({});
    }
  };

  const loadProductMappingsInBackground = async (materials: any[]) => {
    const requests = materials.map(async (material) => {
      const rawMaterialId = Number(material?.id ?? material?.rawMaterialId ?? material?.rawMaterialID);

      if (!Number.isFinite(rawMaterialId) || rawMaterialId <= 0) {
        return null;
      }

      const mapping = await RawMaterialService.getRawMaterialProductMapping(rawMaterialId);
      if (!mapping) {
        return null;
      }

      return {
        rawMaterialId,
        mapping: normalizeProductMapping(rawMaterialId, mapping),
      };
    });

    const results = await Promise.all(requests);
    const lookup: Record<number, RawMaterialProductMapping> = {};

    results.forEach((item) => {
      if (item) {
        lookup[item.rawMaterialId] = item.mapping;
      }
    });

    return lookup;
  };

  const getSearchableText = (item: any) => {
    const rawMaterialId = Number(item?.id ?? item?.Id ?? item?.rawMaterialId ?? item?.rawMaterialID ?? 0);
    const mapping = rawMaterialId ? productMappingByRawMaterialId[rawMaterialId] : null;
    const productNames = mapping?.productNames?.join(" ") || item?.productNames || item?.productName || item?.ProductName || "";

    return [
      ...Object.values(item),
      productNames,
    ]
      .map((value) => String(value ?? ""))
      .join(" ")
      .toLowerCase();
  };

  const normalizeSearchText = (text: string) => text.trim().toLowerCase();

  const handleSearch = (searchText: string) => {
    const normalizedSearch = normalizeSearchText(searchText);
    if (!normalizedSearch) {
      setFilteredRawMaterials(rawMaterials);
      return;
    }

    const filtered = rawMaterials.filter((item) =>
      getSearchableText(item).includes(normalizedSearch)
    );

    setFilteredRawMaterials(filtered);
  };

  return (
    <>
      <style>{`
          .complaint-header-card {
              background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
              border-radius: 16px;
              padding: 1.5rem 2rem;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
              border: 1px solid rgba(34, 197, 94, 0.1);
              margin-bottom: 1.5rem;
          }

          .complaint-title-wrapper {
              display: flex;
              align-items: center;
              gap: 1.25rem;
          }

          .complaint-icon-wrapper {
              width: 56px;
              height: 56px;
              background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
              border-radius: 14px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 8px 16px rgba(76, 175, 80, 0.25);
          }

          .complaint-icon-wrapper i {
              color: white;
              font-size: 1.75rem;
          }

          .add-btn-custom {
              background: #4CAF50 !important;
              border: none !important;
              padding: 0.75rem 1.75rem !important;
              font-weight: 600 !important;
              border-radius: 12px !important;
              box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
              transition: all 0.2s ease;
          }

          .add-btn-custom:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
          }

          @media screen and (max-width: 768px) {
              .complaint-header-card .flex {
                  flex-direction: column;
                  align-items: flex-start !important;
                  gap: 1.5rem;
              }
          }
      `}</style>
      <div className="layout-content mt-3 mb-3">
        <div className="complaint-header-card">
          <div className="flex align-items-center justify-content-between">
            <div className="complaint-title-wrapper">
              <div className="complaint-icon-wrapper">
                <i className="pi pi-box"></i>
              </div>
              <div>
                <h1 className="text-900 font-bold text-3xl m-0 tracking-tight">
                  {t('Raw Materials Master')}
                </h1>
                <p className="text-600 m-0 text-base mt-1 font-medium opacity-80">
                  Track, manage, and monitor raw materials efficiently
                </p>
              </div>
            </div>
            <div>
              <Button
                onClick={() => setShowAddModal(true)}
                icon="pi pi-plus"
                label="Add Raw Material"
                className="add-btn-custom"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-table-mobile mt-4">
          <RawMaterialSearch onSearch={setSelectedKeyword}
            showAddModal={showAddModal} setShowAddModal={setShowAddModal}
            existingMaterials={rawMaterials}
            onSaved={fetchRawMaterials}
          />
          <RawMaterialDatatable
            rawMaterials={filteredRawMaterials}
            setRawMaterials={setRawMaterials}
            productMappingByRawMaterialId={productMappingByRawMaterialId}
            setProductMappingByRawMaterialId={setProductMappingByRawMaterialId}
          />
        </div>
      </div>
    </>
  );
};

export default RawMaterialsMainPage;
